import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { collection, limit, onSnapshot, orderBy, query } from "firebase/firestore";
import { dbAdmin } from "../firebaseConfig";
import "../styles/KempenPublic.css";

const fmtRM = (n) => {
    const x = Number(n || 0);
    return `RM${x.toLocaleString("ms-MY")}`;
};

const pickFeatured = (list = [], featuredId = null) => {
    if (!Array.isArray(list) || list.length === 0) return null;

    if (featuredId) {
        const f = list.find((k) => k?.id === featuredId);
        if (f) return f;
    }

    return list
        .slice()
        .sort((a, b) => (b?.updatedAt || b?.createdAt || 0) - (a?.updatedAt || a?.createdAt || 0))[0];
};

export default function PublicKempenFeatured({ masjid, fallbackImage, onDonate }) {
    const navigate = useNavigate();
    const { slug } = useParams();

    const masjidId = masjid?.id || null;

    const featured = useMemo(() => {
        const node = masjid?.kempen || {};
        const raw = Array.isArray(node?.senarai) ? node.senarai : [];
        const featuredId = node?.featuredId || null;

        const active = raw.filter((k) => k && k.aktif !== false);
        return pickFeatured(active, featuredId);
    }, [masjid]);

    // ===== optional: kira "raised" dari derma_rekod (kalau rules allow)
    const [jumlahByKempen, setJumlahByKempen] = useState(new Map());

    useEffect(() => {
        if (!masjidId) return;

        const qRef = query(
            collection(dbAdmin, "masajid", masjidId, "derma_rekod"),
            orderBy("createdAt", "desc"),
            limit(200)
        );

        const unsub = onSnapshot(
            qRef,
            (snap) => {
                const map = new Map();
                snap.docs.forEach((d) => {
                    const r = d.data() || {};
                    const kempenId = r.kempenId || r.campaignId || null;
                    if (!kempenId) return;

                    const amaun = Number(r.amaun ?? r.amount ?? 0) || 0;
                    const status = String(r.status || "").toLowerCase();
                    const ok = status === "berjaya" || status === "paid" || status === "success";
                    if (!ok) return;

                    map.set(kempenId, (map.get(kempenId) || 0) + amaun);
                });
                setJumlahByKempen(map);
            },
            () => setJumlahByKempen(new Map())
        );

        return () => unsub();
    }, [masjidId]);

    if (!featured) return null;

    const raised = Number(jumlahByKempen.get(featured.id) || 0) || 0;
    const goal = Number(featured?.sasaran || 0) || 0;
    const hasGoal = goal > 0;
    const percent = hasGoal ? Math.max(0, Math.min(100, Math.round((raised / goal) * 100))) : 0;

    const img = featured.imejUrl || fallbackImage || "/images/150.png";

    const handleDonate = () => {
        // ✅ kalau parent pass onDonate, kita ikut itu (direct payment)
        if (typeof onDonate === "function") return onDonate(featured);

        // fallback lama (detail page)
        navigate(`/${slug}/kempen/${featured.id}`);
    };

    return (
        <div className="mp-kempenWrap">
            <div className="kpCard">
                <div className="kpCard-img">
                    <img
                        src={img}
                        alt={featured.tajuk || "Kempen"}
                        onError={(e) => (e.currentTarget.src = fallbackImage || "/images/150.png")}
                    />
                    <div className="kpPill">Derma</div>
                </div>

                <div className="kpCard-body">
                    <div className="kpCard-title">{featured.tajuk || "Kempen Derma"}</div>

                    <div className="kpCard-desc">
                        {featured.keterangan
                            ? featured.keterangan
                            : "Kempen derma sedang dibuka. Terima kasih atas sokongan anda."}
                    </div>

                    {hasGoal ? (
                        <div className="kpBox">
                            <div className="kpBox-row">
                                <div className="kpBox-label">Donation</div>
                                <div className="kpBox-pct">{percent}%</div>
                            </div>

                            <div className="kpBar">
                                <div className="kpBar-fill" style={{ width: `${percent}%` }} />
                            </div>

                            <div className="kpBox-row2">
                                <div className="kpBox-amt">
                                    <div className="kpBox-amtLabel">Raised:</div>
                                    <div className="kpBox-amtValue">{fmtRM(raised)}</div>
                                </div>

                                <div className="kpBox-amt right">
                                    <div className="kpBox-amtLabel">Goal:</div>
                                    <div className="kpBox-amtValue">{fmtRM(goal)}</div>
                                </div>
                            </div>

                            <button className="kpBtn" onClick={handleDonate}>
                                Derma Sekarang
                            </button>
                        </div>
                    ) : (
                        <button className="kpBtn" onClick={handleDonate}>
                            Derma Sekarang
                        </button>
                    )}
                </div>
            </div>

            <div
                className="kpLihatLain"
                role="button"
                tabIndex={0}
                onClick={() => navigate(`/${slug}/kempen`)}
                onKeyDown={(e) => e.key === "Enter" && navigate(`/${slug}/kempen`)}
            >
                Lihat Lagi
            </div>
        </div>
    );
}
